import {EditKpiStatus, EventType} from '@hconnect/janus/src/enums'
import {
  Comment,
  EditedKpi,
  EditPssKpi,
  Event,
  FinalValue,
  JanusPageType
} from '@hconnect/janus/src/types'
import {StoppageDto} from '@hconnect/janus/src/types/stoppages'
import MockAdapter from 'axios-mock-adapter'
import moment from 'moment'

import {numberAndStringRegEx, numberRegEx, saveScenario, sleepResponse, uuidRegEx} from './utils'

import {mockStore} from '@settings/__mock__/mockStore'

const regexDate = '\\d{4}-(0[1-9]|1[0-2])(\\-\\d{2}(T\\d{2}:\\d{2}:\\d{2}(\\.\\d{3})?Z?)?)?'
const regexDateWithoutTimeZone = '\\d{4}-\\d{2}-\\d{2}'

export const enableJanusEndpoints = (mock: MockAdapter) => {
  mock
    .onGet(new RegExp(`/janus-kpi-events/${numberRegEx}/events/${uuidRegEx}/events$`))
    .reply(() => {
      const {
        burglengenfeld: {kpiDailyEvents}
      } = mockStore.scenario()

      return sleepResponse([200, kpiDailyEvents])
    })

  mock
    .onGet(new RegExp(`^/janus-kpi-events/${numberRegEx}/events/${regexDate}/comments$`))
    .reply(() => {
      const {
        burglengenfeld: {kpiDailyComments}
      } = mockStore.scenario()

      return sleepResponse([200, kpiDailyComments])
    })

  mock.onPost(new RegExp(`^/shifthandover/${numberRegEx}/stoppages$`)).reply((stoppage) => {
    const {title} = Object.fromEntries((stoppage?.data as FormData).entries()) as Record<
      string,
      string
    >

    const scenario = mockStore.scenario()
    const template = scenario?.burglengenfeld?.stoppages?.events?.[0] ?? {}

    const newStoppage = {
      ...template,
      id: 'new-stoppage-id',
      title
    }

    if (scenario?.burglengenfeld?.stoppages?.events) {
      scenario.burglengenfeld.stoppages.events = [
        ...scenario.burglengenfeld.stoppages.events,
        newStoppage
      ]
      saveScenario(scenario)
    }

    return sleepResponse([201, newStoppage])
  })

  mock.onGet(new RegExp(`/shifthandover/${numberRegEx}/process-stages$`)).reply(() => {
    const {
      burglengenfeld: {processStages}
    } = mockStore.scenario()

    return sleepResponse([200, processStages])
  })

  mock.onGet(new RegExp(`/janus-kpi/administration/periods/${numberRegEx}/status$`)).reply(() => {
    const {
      burglengenfeld: {selectedPeriodStatus}
    } = mockStore.scenario()

    return sleepResponse([200, selectedPeriodStatus])
  })

  mock.onGet(new RegExp(`^/kms/plants/${numberAndStringRegEx}`)).reply(() => {
    const {
      burglengenfeld: {upm}
    } = mockStore.scenario()

    return sleepResponse([200, upm.plantData])
  })

  mock.onGet(new RegExp(`/plantconfig/${numberRegEx}/mainequipmentnumbers$`)).reply(() => {
    const {
      burglengenfeld: {mainEquipments}
    } = mockStore.scenario()

    return sleepResponse([200, mainEquipments])
  })

  mock.onPost(new RegExp(`^/shifthandover/${numberRegEx}/search/events`)).reply(() => {
    const {
      burglengenfeld: {stoppages}
    } = mockStore.scenario()

    return sleepResponse([200, stoppages])
  })

  mock.onPost(new RegExp(`/stoppages/${uuidRegEx}/new-janus-stoppages`)).reply((stoppage) => {
    const scenario = mockStore.scenario()
    const {title, planningSchedule} = JSON.parse(stoppage.data as string) as StoppageDto
    const newStoppage = {
      ...scenario.burglengenfeld.pssStoppages[0],
      id: 'new-stoppage-id',
      title,
      planningSchedule
    }
    scenario.burglengenfeld.pssStoppages = [...scenario.burglengenfeld.pssStoppages, newStoppage]
    saveScenario(scenario)
    return sleepResponse([201, newStoppage])
  })

  mock.onPost(new RegExp('/stoppages/stoppage-codes')).reply(() => {
    const {
      burglengenfeld: {stoppageCodes}
    } = mockStore.scenario()

    return sleepResponse([200, stoppageCodes])
  })

  mock.onPost(new RegExp('^/stoppages$')).reply((stoppage) => {
    const payload = JSON.parse(stoppage.data as string)

    const stoppageId = payload.filters.find(
      (filter: {property: string}) => filter.property === 'Id'
    )?.values?.[0]
    const planningScheduleValue = payload.filters.find(
      (filter: {property: string}) => filter.property === 'PlanningSchedule'
    )?.values?.[0]

    const {
      burglengenfeld: {pssStoppages}
    } = mockStore.scenario()

    if (stoppageId) {
      return sleepResponse([200, pssStoppages.filter((s) => s.id === stoppageId)])
    }

    return sleepResponse([
      200,
      pssStoppages.filter((s) => s.planningSchedule === planningScheduleValue)
    ])
  })

  mock
    .onGet(
      new RegExp(
        `^/janus-monthly-result/janus-kpi/${numberRegEx}/treeStructure\\?date=${regexDate}&versionSelection=${numberRegEx}`
      )
    )
    .reply(() => {
      const {
        burglengenfeld: {monthlyEntriesCategories}
      } = mockStore.scenario()

      return sleepResponse([200, monthlyEntriesCategories])
    })

  mock
    .onGet(new RegExp(`^/janus-kpi-events/${numberRegEx}/events/${regexDate}/warnings$`))
    .reply(() => {
      const {
        burglengenfeld: {warnings}
      } = mockStore.scenario()

      return sleepResponse([200, warnings])
    })
  mock
    .onGet(
      new RegExp(
        `^/janus-pss/janus-kpi/${numberRegEx}/treeStructure\\?date=${regexDate}&versionSelection=${numberRegEx}`
      )
    )
    .reply(() => {
      const {
        burglengenfeld: {pssEntriesCategories}
      } = mockStore.scenario()

      return sleepResponse([200, pssEntriesCategories])
    })

  mock.onGet(new RegExp(`^/janus-pss/janus-kpi/${numberRegEx}\\?`)).reply((config) => {
    const params = new URLSearchParams(config.url!.split('?')[1])

    const includeAuxiliary = params.get('includeAuxiliary') === 'true'

    const {
      burglengenfeld: {pssKpis, pssKpisWithAux}
    } = mockStore.scenario()

    return sleepResponse([200, includeAuxiliary ? pssKpisWithAux : pssKpis])
  })

  mock
    .onGet(
      new RegExp(
        `^/janus-monthly-result/janus-kpi/${numberRegEx}/${regexDateWithoutTimeZone}/${uuidRegEx}/${numberRegEx}$`
      )
    )
    .reply(() => {
      const {
        burglengenfeld: {monthlyResultKpis}
      } = mockStore.scenario()

      return sleepResponse([200, monthlyResultKpis])
    })

  mock
    .onGet(new RegExp(`^/janus-daily-kpi/${numberRegEx}/daily/${regexDate}/calendar-info/1$`))
    .reply(() => {
      const {
        burglengenfeld: {kpiMonthlyCalendar}
      } = mockStore.scenario()

      return sleepResponse([200, kpiMonthlyCalendar])
    })

  mock.onGet(new RegExp(`^/janus-daily-kpi/${numberRegEx}/cumulated/${regexDate}`)).reply(() => {
    const {
      burglengenfeld: {kpiCumulatedDailyEntries}
    } = mockStore.scenario()

    return sleepResponse([200, kpiCumulatedDailyEntries])
  })

  mock
    .onGet(new RegExp(`^/janus-daily-kpi/${numberRegEx}/daily/${regexDate}/calendar-info/2$`))
    .reply(() => {
      const {
        burglengenfeld: {kpiWeeklyCalendar}
      } = mockStore.scenario()

      return sleepResponse([200, kpiWeeklyCalendar])
    })

  mock
    .onGet(
      new RegExp(
        `/asset-config/janus-production-data/plant/${uuidRegEx}/upm-type/Department\\?typeUpmId=${uuidRegEx}`
      )
    )
    .reply(() => {
      const {
        burglengenfeld: {departmentConfig}
      } = mockStore.scenario()

      return sleepResponse([200, departmentConfig])
    })

  mock
    .onGet(
      new RegExp(
        `/asset-config/janus-production-data/plant/${uuidRegEx}/upm-type/Plant\\?typeUpmId=${uuidRegEx}`
      )
    )
    .reply(() => {
      return sleepResponse([200, {payload: {productionData: []}}])
    })

  mock
    .onPost(
      new RegExp(
        `/asset-config/janus-production-data/plant/${uuidRegEx}/upm-type/Department\\?typeUpmId=${uuidRegEx}`
      )
    )
    .reply((config) => {
      const body = JSON.parse(config.data as string)
      return sleepResponse([200, {payload: body}])
    })

  mock
    .onPut(
      new RegExp(
        `/asset-config/janus-production-data/plant/${uuidRegEx}/upm-type/Plant\\?typeUpmId=${uuidRegEx}`
      )
    )
    .reply((config) => {
      const body = JSON.parse(config.data as string)
      return sleepResponse([200, {payload: body}])
    })

  mock
    .onPost(
      new RegExp(
        `/asset-config/janus-production-data/plant/${uuidRegEx}/upm-type/Plant\\?typeUpmId=${uuidRegEx}`
      )
    )
    .reply((config) => {
      const body = JSON.parse(config.data as string)
      return sleepResponse([200, {payload: body}])
    })

  mock
    .onPut(
      new RegExp(
        `/asset-config/janus-production-data/plant/${uuidRegEx}/upm-type/Department\\?typeUpmId=${uuidRegEx}`
      )
    )
    .reply((config) => {
      const body = JSON.parse(config.data as string)
      return sleepResponse([200, {payload: body}])
    })

  mock
    .onGet(
      new RegExp(
        `/asset-config/janus-production-data/plant/${uuidRegEx}/upm-type/Area\\?typeUpmId=${uuidRegEx}`
      )
    )
    .reply(() => {
      const {
        burglengenfeld: {areaConfig}
      } = mockStore.scenario()

      return sleepResponse([200, areaConfig])
    })

  mock
    .onPut(
      new RegExp(
        `/asset-config/janus-production-data/plant/${uuidRegEx}/upm-type/Area\\?typeUpmId=${uuidRegEx}`
      )
    )
    .reply((config) => {
      const scenario = mockStore.scenario()
      const body = JSON.parse(config.data as string) as {
        productionData: Array<{name: string; value: string}>
      }
      scenario.burglengenfeld.areaConfig = {
        ...scenario.burglengenfeld.areaConfig,
        payload: body
      }
      saveScenario(scenario)
      return sleepResponse([200, scenario.burglengenfeld.areaConfig])
    })

  mock
    .onPost(
      new RegExp(
        `/asset-config/janus-production-data/plant/${uuidRegEx}/upm-type/Area\\?typeUpmId=${uuidRegEx}`
      )
    )
    .reply((config) => {
      const scenario = mockStore.scenario()
      const body = JSON.parse(config.data as string) as {
        productionData: Array<{name: string; value: string}>
      }
      scenario.burglengenfeld.areaConfig = {
        ...scenario.burglengenfeld.areaConfig,
        payload: body
      }
      saveScenario(scenario)
      return sleepResponse([200, scenario.burglengenfeld.areaConfig])
    })

  mock
    .onPost(new RegExp(`^/janus-kpi-events/${numberRegEx}/events/${uuidRegEx}/comments$`))
    .reply((config) => {
      const scenario = mockStore.scenario()
      const {value} = JSON.parse(config.data as string) as Comment

      const newComment: Comment = {
        commentDate: moment.utc().toISOString(),
        id: Date.now().toString(),
        kpiName: 'kpi name for new comment',
        kpiId: '1',
        userName: 'User Name',
        value,
        commentParentId: null
      }
      const newEvent: Event = {
        eventDate: moment.utc().toISOString(),
        eventType: EventType.Comment,
        id: Date.now().toString(),
        kpiName: 'kpi name for new comment',
        version: '1',
        userName: 'User Name',
        value
      }

      scenario.burglengenfeld.kpiDailyComments.janusComments = [
        ...scenario.burglengenfeld.kpiDailyComments.janusComments,
        newComment
      ]
      scenario.burglengenfeld.kpiDailyEvents.events = [
        ...scenario.burglengenfeld.kpiDailyEvents.events,
        newEvent
      ]

      saveScenario(scenario)
      return sleepResponse([201, newComment])
    })

  mock
    .onPost(
      new RegExp(
        '/janus-pss/janus-kpi/0014/2025-04-01T00:00:00/3fa85f64-5717-4562-b3fc-2c963f66afa8'
      )
    )
    .reply(() => {
      const kpis: EditedKpi[] = [
        {
          id: '1',
          status: EditKpiStatus.Success,
          specificKpiId: '1',
          groupId: '1'
        }
      ]

      return sleepResponse([201, kpis])
    })

  mock
    .onPost(new RegExp(`^/janus-pss/janus-kpi/${numberRegEx}/${regexDate}/${uuidRegEx}`))
    .reply((config) => {
      const scenario = mockStore.scenario()
      const requestKpis = JSON.parse(config.data as string) as EditPssKpi[]

      const newPssKpis = scenario.burglengenfeld.pssKpis.kpiGroups[0].kpis

      if (newPssKpis?.[0]?.values?.[0] && newPssKpis?.[0]?.values[1]) {
        if (requestKpis[1]?.value) {
          newPssKpis[0].values[1].value = requestKpis[1].value
        } else {
          newPssKpis[0].values[1].value = requestKpis[0].value
        }
      }

      const kpis: EditedKpi[] = newPssKpis.map((entry) => {
        return {
          id: entry.specificKpiId,
          status: EditKpiStatus.Success,
          specificKpiId: entry.specificKpiId
        }
      })
      scenario.burglengenfeld.pssKpis.kpiGroups[0].kpis = newPssKpis

      const response: EditedKpi[] = kpis

      saveScenario(scenario)
      return sleepResponse([201, response])
    })

  mock
    .onPatch(new RegExp(`/janus-daily-kpi/${numberRegEx}/daily/${regexDate}/approve`))
    .reply(() => {
      const scenario = mockStore.scenario()
      const newCalendarInfo = {...scenario.burglengenfeld.kpiMonthlyCalendar}

      newCalendarInfo.days = scenario.burglengenfeld.kpiMonthlyCalendar.days.map((day) => {
        return {...day, status: 1}
      })

      scenario.burglengenfeld.kpiMonthlyCalendar = newCalendarInfo
      saveScenario(scenario)

      return sleepResponse([201, 1])
    })

  mock
    .onPatch(new RegExp(`/janus-pss/janus-kpi/${numberRegEx}/${regexDate}/${numberRegEx}/approve`))
    .reply(() => {
      return sleepResponse([201, 1])
    })

  mock
    .onPost(new RegExp(`^/janus-daily-kpi/{numberRegEx}/cumulated/${regexDate}$`))
    .reply((config) => {
      const scenario = mockStore.scenario()

      scenario.burglengenfeld.kpiCumulatedDailyEntries.finalValues = JSON.parse(
        config.data as string
      ) as FinalValue[]

      saveScenario(scenario)
      return sleepResponse([201, {}])
    })

  mock.onGet(new RegExp(`^/janus-daily-kpi/${numberRegEx}/daily/2024-07-20T00:00:00`)).reply(() => {
    const {
      burglengenfeld: {kpiDailyEntriesSaved}
    } = mockStore.scenario()

    return sleepResponse([200, kpiDailyEntriesSaved])
  })

  mock
    .onGet(new RegExp(`^/janus-daily-kpi/${numberRegEx}/daily/${regexDate}(\\?.*)?$`))
    .reply(() => {
      const {
        burglengenfeld: {kpiDailyEntries}
      } = mockStore.scenario()

      return sleepResponse([200, kpiDailyEntries])
    })

  mock.onGet(new RegExp(`^/shifthandover/${numberRegEx}/config`)).reply(() => {
    const {
      burglengenfeld: {shiftHandoverConfig}
    } = mockStore.scenario()

    return sleepResponse([200, shiftHandoverConfig])
  })

  mock
    .onGet(new RegExp(`/shifthandover/${numberRegEx}/events/${numberAndStringRegEx}`))
    .reply(() => {
      const {
        burglengenfeld: {stoppageDetails}
      } = mockStore.scenario()

      return sleepResponse([200, stoppageDetails[0]])
    })

  mock
    .onGet(
      new RegExp(
        `/janus-kpi-events/${numberRegEx}/messages\\?date=${regexDate}&pageType=${numberRegEx}`
      )
    )
    .reply((config) => {
      const url = new URL(config.url!, 'http://localhost')
      const pageTypeParam = url.searchParams.get('pageType')

      const {
        burglengenfeld: {plantMessages}
      } = mockStore.scenario()

      const pageType = Number(pageTypeParam)

      const filteredMessages = plantMessages.filter(
        (message) => message.pageType === (pageType as JanusPageType)
      )

      return sleepResponse([200, filteredMessages])
    })

  mock
    .onPut(
      new RegExp(`/shifthandover/${numberAndStringRegEx}/events/${numberAndStringRegEx}/submitted`)
    )
    .reply((config) => {
      const scenario = mockStore.scenario()
      const eventId = (config.url as string).split('/')[4]

      const {stoppageDetails} = scenario.burglengenfeld
      scenario.burglengenfeld.stoppageDetails = stoppageDetails.map((stoppage) =>
        stoppage.id === eventId ? {...stoppage, isSubmitted: true} : stoppage
      )
      const submittedStoppage = stoppageDetails.find((s) => s.id === eventId)

      saveScenario(scenario)
      return sleepResponse([200, submittedStoppage])
    })
  return mock
}
